import axios from 'axios'
import { mergeAgentsQueryIntoUrl } from '@/utils/agentsApi'

// PAICE chat has pre-conditional check for settings concurrency
export const NO_SETTINGS_STORED = '*'
export const settingsVersionToken = (settings) => settings?.updatedAt || NO_SETTINGS_STORED

/**
 * Build the conditional header asserting which settings version a chat send is
 * based on.
 *
 * Exclusive to `POST .../aquabot`: the stream is the one place a mid-compose
 * settings change still matters, because answering under a prompt/model the user
 * never saw silently contradicts what the UI showed.
 *
 * A concrete `updatedAt` goes in `If-Match`, so the send is rejected (412) if
 * someone else has written since. When the caller read no stored settings there
 * is no validator to echo, so `If-None-Match: *` asserts "still unconfigured"
 * and catches a concurrent first-time create. Sending neither header (only when
 * the read itself failed, so there is genuinely nothing to assert) skips the
 * check server-side.
 *
 * @param {?string} expectedUpdatedAt - updatedAt from the last read, the '*'
 *        sentinel for "nothing stored", or null/undefined to skip the check
 */
export function settingsPreconditionHeaders(expectedUpdatedAt) {
    if (!expectedUpdatedAt) return {}
    return expectedUpdatedAt === NO_SETTINGS_STORED
        ? { 'If-None-Match': NO_SETTINGS_STORED }
        : { 'If-Match': expectedUpdatedAt }
}

const store = {
    namespaced: true,
    state: {
        // Agent settings
        paiceSettings: null,
        availableModels: [],
        modelCatalogSource: null,
        modelCatalogRegion: null,

        // The cid's context layer, as authored. `null` means "not fetched yet";
        // an empty string means "fetched, and this cid has never authored one"
        // (the service 404s in that case, which is not an error).
        contextLayer: null,

        // Roles
        standardRoles: [1, 1.5, 2, 3], // Can view, create, and modify resources
        privilegedRoles: [1, 1.5, 3], // Can perform privileged operations also (e.g., chat deletes, settings/context layer updates)
        // Aqfer's own staff, as opposed to a customer's administrator. Narrower
        // than privilegedRoles on purpose: client_admin (3) administers one
        // client's data, and the one thing gated on this — declaring an MCP
        // server trusted enough to receive the caller's Aqfer token — is a
        // statement about Aqfer's trust boundary rather than about theirs.
        aqferStaffRoles: [1, 1.5],
        // Hosts an MCP server may live on and still be sent the caller's Aqfer
        // access token (authType: bearer_passthrough). Suffix match with a dot
        // boundary. Mirrors MCP_BEARER_PASSTHROUGH_HOSTS in the agents service,
        // which is the authoritative check — this list only keeps the UI from
        // offering a combination the service will refuse.
        bearerPassthroughHosts: ['aqfer.net', 'aqfer.com'],

        // Chat history result limits
        chatLimit: 50,
        adminChatLimit: 50,

        // Cached chat lists, kept separate so switching scope does not refetch.
        // `null` means "never fetched" — the only state that triggers a load;
        // an empty array is a real result (this cid/user has no chats).
        chats: null,
        adminChats: null,

        // --- MCP servers ---
        // Server definitions visible to this user (the cid's shared ones plus
        // their own private ones). `null` means "never fetched"; `[]` means
        // this cid has none registered.
        mcpServers: null,
        // The server ids switched on for the cid, defaults already resolved by
        // the service — so Aqfer's built-in servers are in here unless one was
        // explicitly switched off. Cid-wide, not per user: everyone on the cid
        // has the same tools available.
        mcpEnabledIds: [],
        // Credential status per serverId, keyed the way the list renders it.
        // Never holds a secret — the service returns status and metadata only.
        mcpCredentials: {},
        // Tool catalog + resolved permissions per serverId, as the tools
        // endpoint returned it. Cached because listing a server's tools means
        // dialling it, which is far too slow to repeat on every render.
        mcpServerTools: {},
    },
    mutations: {
        setPaiceSettings(state, settings) {
            state.paiceSettings = settings
        },
        setModelCatalog(state, payload) {
            state.availableModels = payload?.models || []
            state.modelCatalogSource = payload?.source || null
            state.modelCatalogRegion = payload?.region || null
        },
        setContextLayer(state, yamlText) {
            state.contextLayer = yamlText
        },
        setChatLimit(state, limit) {
            state.chatLimit = limit
        },
        setAdminChatLimit(state, limit) {
            state.adminChatLimit = limit
        },
        setChats(state, chats) {
            state.chats = chats
        },
        setAdminChats(state, chats) {
            state.adminChats = chats
        },
        setMcpServers(state, servers) {
            state.mcpServers = servers
        },
        // The cid's switched-on set, not the current user's — see the
        // getMcpServers/setMcpServerEnabled actions below.
        setMcpEnabledIds(state, ids) {
            state.mcpEnabledIds = ids || []
        },
        setMcpCredentials(state, byServerId) {
            state.mcpCredentials = byServerId || {}
        },
        setMcpCredential(state, { serverId, credential }) {
            state.mcpCredentials = { ...state.mcpCredentials, [serverId]: credential }
        },
        setMcpServerTools(state, payloadOrEntry) {
            // Called two ways: with `{ serverId, payload }` to record one
            // server's catalog, and with a whole map (`{}`) to reset the lot
            // when the client changes.
            if (payloadOrEntry && 'serverId' in payloadOrEntry) {
                const { serverId, payload } = payloadOrEntry
                state.mcpServerTools = { ...state.mcpServerTools, [serverId]: payload }
                return
            }
            state.mcpServerTools = payloadOrEntry || {}
        },
    },
    actions: {
        onClientChange({ commit }) {
            commit('setPaiceSettings', null)
            commit('setModelCatalog', null)
            commit('setContextLayer', null)
            commit('setChats', null)
            commit('setAdminChats', null)
            commit('setMcpServers', null)
            commit('setMcpEnabledIds', [])
            commit('setMcpCredentials', {})
            commit('setMcpServerTools', {})
        },
        setChatLimit({ commit }, limit) {
            commit('setChatLimit', limit)
        },
        setAdminChatLimit({ commit }, limit) {
            commit('setAdminChatLimit', limit)
        },

        /**
         * Fetch the chat list for the current cid, newest first.
         *
         * Cached per scope: a already-populated list is returned untouched unless
         * `forceGet` is set, so toggling between "Mine" and "All users" costs one
         * request each per session rather than one per toggle. Callers that
         * mutate the list (new chat, delete, retitle) pass `forceGet` or commit
         * the change directly.
         *
         * @param {Object} [payload]
         * @param {boolean} [payload.adminHistory=false] - every user's chats
         * @param {boolean} [payload.forceGet=false] - refetch even if cached
         * @param {number} [payload.limit] - max chats to return
         */
        async getChats({ commit, state, rootState }, { adminHistory = false, forceGet = false, limit } = {}) {
            const cached = adminHistory ? state.adminChats : state.chats
            if (!forceGet && cached != null) return cached

            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/chats`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
                params: {
                    ...(limit ? { limit } : {}),
                    ...(adminHistory ? { admin_history: true } : {}),
                },
            }).then((response) => {
                const chats = (response?.data?.items || []).sort((a, b) => {
                    return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
                })
                commit(adminHistory ? 'setAdminChats' : 'setChats', chats)
                return chats
            }).catch((e) => {
                console.error("Error fetching paice chats: ", e)
                throw e;
            })
        },

        /** Replace the cached list for one scope (after a create/delete/retitle). */
        setChats({ commit }, { chats, adminHistory = false } = {}) {
            commit(adminHistory ? 'setAdminChats' : 'setChats', chats)
        },

        /**
         * Bedrock models this account can invoke, for the model picker. This is a
         * convenience list and not an allowlist — writes accept any id — so a
         * failure here must not block the settings page.
         */
        async getAvailableModels({ commit, rootState }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/aquabot/models`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setModelCatalog', response?.data || {})
                return response
            }).catch((e) => {
                console.error("Error fetching paice model catalog: ", e)
                throw e;
            })
        },

        /**
         * Get all stored settings.
         *
         * @param {Object} [payload]
         * @param {boolean} [payload.commit=true] - pass false to read the stored
         *        settings without making them the ones in effect. The chat's
         *        conflict notice needs to show what changed before the user has
         *        agreed to run under it, so it reads with commit: false and
         *        commits via `applyPaiceSettings` only on accept.
         */
        async getPaiceSettings({ commit, rootState }, { commit: shouldCommit = true } = {}) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/settings`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                let settings = response?.data || {};
                if (shouldCommit) {
                    commit('setPaiceSettings', settings)
                }
                return response
            }).catch((e) => {
                console.error("Error fetching paice settings: ", e)
                throw e;
            })
        },

        /** Make an already-fetched settings object the one in effect. */
        applyPaiceSettings({ commit }, settings) {
            commit('setPaiceSettings', settings || null)
        },

        /**
         * Create or replace settings for the current cid.
         *
         * @param {Object} payload
         * @param {?string} payload.systemPrompt - null/empty clears the override
         * @param {?string} payload.modelId - null/empty reverts to the service default
         *
         * The write is unconditional — the service takes the last writer, so a
         * concurrent edit by another admin is overwritten rather than rejected.
         * Staleness is only enforced on the chat stream, which asserts the version
         * it was composed under (see `settingsPreconditionHeaders`).
         */
        async savePaiceSettings({ commit, rootState }, { systemPrompt, modelId }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/settings`, rootState);
            return axios.put(endpoint, {
                systemPrompt: systemPrompt || null,
                modelId: modelId || null,
            }, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setPaiceSettings', response?.data || {})
                return response
            }).catch((e) => {
                console.error("Error saving paice settings: ", e)
                throw e;
            })
        },

        /**
         * Delete all stored settings, reverting the cid to service defaults.
         *
         * Unconditional, like the write: the service 404s only when there was
         * nothing stored to delete.
         */
        async deletePaiceSettings({ commit, rootState }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/settings`, rootState);
            return axios.delete(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setPaiceSettings', response?.data || {})
                return response
            }).catch((e) => {
                console.error("Error deleting paice settings: ", e)
                throw e;
            })
        },

        /**
         * Fetch the cid's context layer — the semantic description of its own
         * datasets, metrics and joins that grounds PAICE's answers.
         *
         * The service returns the raw YAML byte-for-byte as authored, not a JSON
         * envelope, so the response is read as text. A 404 means this cid has
         * never authored one, which is a normal empty state rather than a
         * failure: it resolves to "" so callers can render the blank editor.
         */
        async getContextLayer({ commit, rootState }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/context`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                    Accept: 'application/yaml',
                },
                // Without this axios sniffs the body and would hand back a parsed
                // object for anything JSON-ish, losing the authored formatting.
                responseType: 'text',
                transformResponse: [(data) => data],
            }).then((response) => {
                // The global interceptor resolves with `undefined` when a request
                // fails without a response (network/CORS). Treating that as an
                // empty layer would show the "not configured" state for a cid
                // that has one, and let the next save silently replace it.
                if (!response) {
                    throw new Error('No response received while fetching the context layer')
                }
                const text = typeof response.data === 'string' ? response.data : ''
                commit('setContextLayer', text)
                return text
            }).catch((e) => {
                if (e?.response?.status === 404) {
                    // Never authored one — an empty state, not an error.
                    commit('setContextLayer', '')
                    return ''
                }
                console.error("Error fetching paice context layer: ", e)
                throw e;
            })
        },

        /**
         * Validate and store the context layer for the current cid.
         *
         * The body is the YAML document itself rather than a JSON envelope, and
         * a client has exactly one layer, so this replaces whatever was stored.
         * The service validates strictly (unknown keys are rejected) and returns
         * 400 with every violation flattened to a `domains.0.datasets.2.name`
         * style path; nothing is stored when that happens.
         *
         * @param {string} yamlText - the document exactly as authored
         */
        async saveContextLayer({ commit, rootState }, yamlText) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/context`, rootState);
            return axios.put(endpoint, yamlText, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                    'Content-Type': 'application/yaml',
                },
            }).then((response) => {
                // As in getContextLayer: a resolved-but-empty response means the
                // request never completed, so don't report it as saved.
                if (!response) {
                    throw new Error('No response received while saving the context layer')
                }
                // The response carries only a status message, so the stored copy
                // is what we just sent.
                commit('setContextLayer', yamlText)
                return response
            }).catch((e) => {
                console.error("Error saving paice context layer: ", e)
                throw e;
            })
        },

        // ── MCP servers ──────────────────────────────────────────────────────
        //
        // Two things are stored separately and have separate lifecycles: the
        // *definition* of a server (its URL, auth type, tool prefix — shared
        // across the cid when an admin registers it) and the *credential* used
        // to talk to it (almost always per user, and never returned to the
        // browser once saved). The panel reads both and never conflates them.
        //
        // Which servers are switched on is a property of the *client*, not of
        // the person looking: one set per cid, changed by an admin, and the
        // same for everyone in it. Nothing here is a personal preference.

        /**
         * Every MCP server this user can see, plus which ones the client has
         * switched on. Two requests rather than one because the enabled set is
         * a single cid-wide record and the definitions are a list — they are
         * written by different endpoints and change for different reasons.
         *
         * @param {Object} [payload]
         * @param {boolean} [payload.forceGet=false] - refetch even if cached
         */
        async getMcpServers({ commit, state, rootState }, { forceGet = false } = {}) {
            if (!forceGet && state.mcpServers != null) return state.mcpServers

            const base = `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers`
            const headers = { Authorization: `Bearer ${rootState.accessToken}` }

            return Promise.all([
                axios.get(mergeAgentsQueryIntoUrl(base, rootState), { headers }),
                axios.get(mergeAgentsQueryIntoUrl(`${base}/enabled`, rootState), { headers }),
            ]).then(([listResponse, enabledResponse]) => {
                const servers = listResponse?.data?.servers || []
                commit('setMcpServers', servers)
                commit('setMcpEnabledIds', enabledResponse?.data?.serverIds || [])
                return servers
            }).catch((e) => {
                console.error("Error fetching MCP servers: ", e)
                throw e;
            })
        },

        /**
         * Credential status for one server — never the credential itself.
         *
         * Servers whose auth needs nothing stored answer `not_required`, and
         * ones the user has not connected answer `not_connected`; both are
         * normal states rather than failures, so this resolves either way and
         * only a transport error rejects.
         */
        async getMcpCredential({ commit, rootState }, serverId) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}/credential`,
                rootState,
            );
            return axios.get(endpoint, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                const credential = response?.data || null
                commit('setMcpCredential', { serverId, credential })
                return credential
            }).catch((e) => {
                console.error("Error fetching MCP credential status: ", e)
                throw e;
            })
        },

        async createMcpServer({ rootState }, server) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers`,
                rootState,
            );
            return axios.post(endpoint, server, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => response?.data)
        },

        async updateMcpServer({ rootState }, { serverId, server }) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}`,
                rootState,
            );
            return axios.put(endpoint, server, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => response?.data)
        },

        /** Deleting a definition cascades to every credential saved against it. */
        async deleteMcpServer({ rootState }, serverId) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}`,
                rootState,
            );
            return axios.delete(endpoint, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => response?.data)
        },

        /**
         * Save an API key for a server. The key goes up once and is never sent
         * back down; the response is status metadata only.
         *
         * A 503 here means the deployment has no credential encryption key
         * configured, so it refuses to store secrets at all rather than holding
         * them in the clear. Callers surface that as its own message.
         */
        async saveMcpCredential({ commit, rootState }, { serverId, apiKey }) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}/credential`,
                rootState,
            );
            return axios.put(endpoint, { apiKey }, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                const credential = response?.data || null
                commit('setMcpCredential', { serverId, credential })
                return credential
            })
        },

        /**
         * Begin an OAuth connect: ask the service for a URL to send the user to.
         *
         * The service does all of discovery (401 -> protected-resource metadata
         * -> authorization-server metadata), picks CIMD or DCR, mints the PKCE
         * pair and stores it against a pending credential row before answering.
         * The portal's whole job is to open the returned URL and post back what
         * comes off the redirect.
         *
         * Always per-user. OAuth tokens are identity-bound, so unlike an API
         * key there is no shared-credential variant of this.
         */
        async startMcpOAuth({ rootState }, { serverId, cid } = {}) {
            const forCid = cid || rootState.currentClient.cid
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${forCid}/mcp-servers/${serverId}/oauth/authorize`,
                rootState,
            );
            return axios.post(endpoint, {}, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => response?.data)
        },

        /**
         * Finish an OAuth connect with what the authorization server put on the
         * redirect URI.
         *
         * `iss` MUST be forwarded when present. The service compares it against
         * the issuer it recorded at discovery time *before* it redeems the code
         * (RFC 9207) — dropping it here silently disables the check that closes
         * authorization-server mix-up attacks.
         */
        async completeMcpOAuth({ commit, rootState }, { serverId, code, state, iss, cid }) {
            // Pinned to the cid the connect *started* under, not whatever is
            // selected now. A consent screen can sit open for minutes, and the
            // pending PKCE row lives in the starting client's partition — if the
            // user switched clients in the meantime, resolving the cid here
            // would post the code to a partition that has no matching state.
            const forCid = cid || rootState.currentClient.cid
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${forCid}/mcp-servers/${serverId}/oauth/callback`,
                rootState,
            );
            return axios.post(endpoint, { code, state, iss }, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                const result = response?.data || null
                if (result) {
                    commit('setMcpCredential', { serverId, credential: result })
                }
                return result
            })
        },

        /** Revoke this user's credential. The server definition stays. */
        async deleteMcpCredential({ commit, rootState }, serverId) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}/credential`,
                rootState,
            );
            return axios.delete(endpoint, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                commit('setMcpCredential', {
                    serverId,
                    credential: { serverId, status: 'not_connected' },
                })
                return response?.data
            })
        },

        /**
         * Switch one server on or off for the whole client.
         *
         * Not a personal setting: everyone on the cid gets the tools of every
         * server that is on, so the service admits this only from an admin
         * (the one exception being a user's own private server, which nobody
         * else can see anyway) and answers 403 otherwise.
         *
         * Per server rather than "send the whole set you want to end up with",
         * because Aqfer's built-in servers are on by *default* and an external
         * one is off by default — so omitting an id from a list does not mean
         * the same thing for both, and only the caller knows which way it meant.
         * The response is the resolved set, defaults included.
         */
        async setMcpServerEnabled({ commit, rootState }, { serverId, enabled }) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}/enabled`,
                rootState,
            );
            return axios.put(endpoint, { enabled }, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                const saved = response?.data?.serverIds || []
                commit('setMcpEnabledIds', saved)
                return saved
            })
        },

        /**
         * Dial a server and report what came back.
         *
         * Two shapes, because there are two moments worth testing. Pass a
         * `serverId` to test a saved server with the credential it would really
         * use; pass a `definition` to test one still being typed into the form,
         * before anything is stored.
         *
         * A failed *connection* is a 200 with `ok: false` — the request worked,
         * the server is the problem — so only a genuine API error rejects here.
         */
        async testMcpServer({ rootState }, { serverId, definition } = {}) {
            const base = `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers`
            const endpoint = mergeAgentsQueryIntoUrl(
                serverId ? `${base}/${serverId}/test` : `${base}/test`,
                rootState,
            );
            return axios.post(endpoint, serverId ? {} : definition, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                if (!response) {
                    throw new Error('No response received while testing the server')
                }
                return response.data
            })
        },

        // ── Tool permissions ─────────────────────────────────────────────────
        //
        // One layer only: the client policy, one setting per tool for the whole
        // cid, edited in Settings by whoever may edit the server. Answering a
        // prompt in a chat releases that one call and stores nothing — there is
        // no personal override, so a tool on "ask" asks every time.

        /**
         * A server's tools, each carrying the allow/ask/deny it resolves to.
         *
         * Cached per server: listing tools means the service dials the MCP
         * server, so this is far too slow to repeat on every render. Pass
         * `refresh` to re-read the server itself rather than its cached catalog.
         *
         * A server that cannot be reached still answers 200, with `stale: true`
         * and the last known catalog — the stored permissions are in force
         * whether or not the server is up, so they are worth showing.
         *
         * `force` re-reads without asking the service to re-dial: the two are
         * separate because a policy change alters what the response says about
         * tools that have not moved, and paying for a dial to learn that would
         * be the wrong trade.
         */
        async getMcpServerTools({ commit, state, rootState }, { serverId, refresh = false, force = false } = {}) {
            if (!refresh && !force && state.mcpServerTools[serverId]) return state.mcpServerTools[serverId]

            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}`
                    + `/mcp-servers/${serverId}/tools${refresh ? '?refresh=true' : ''}`,
                rootState,
            );
            return axios.get(endpoint, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                const payload = response?.data || { tools: [] }
                commit('setMcpServerTools', { serverId, payload })
                return payload
            })
        },

        /**
         * Replace the client-wide policy for one server. Full replace, so the
         * caller sends every override it wants to keep — omitting a tool means
         * "fall back to the default", not "leave it as it was".
         *
         * The cached catalog is updated in place from the response rather than
         * refetched, because a refetch would dial the server again just to
         * learn something we already know.
         */
        async saveMcpToolPolicy({ commit, state, rootState }, { serverId, defaultMode, tools }) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}/policy`,
                rootState,
            );
            return axios.put(endpoint, { default: defaultMode, tools }, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => {
                const policy = response?.data
                const cached = state.mcpServerTools[serverId]
                if (cached && policy) {
                    commit('setMcpServerTools', {
                        serverId,
                        payload: {
                            ...cached,
                            policy,
                            // Same resolution the service runs, and there is only
                            // the one layer of it: the tool's own setting, else
                            // the policy default.
                            tools: (cached.tools || []).map((tool) => {
                                const cidMode = policy.tools?.[tool.name] || null
                                return { ...tool, cidMode, mode: cidMode || policy.default }
                            }),
                        },
                    })
                }
                return policy
            })
        },

        /**
         * Put one server back to having no permissions set at all.
         *
         * Deletes the stored policy rather than saving a permissive one,
         * because for Aqfer's built-in servers those are different outcomes: an
         * unconfigured built-in is judged per tool — read-only tools run
         * silently, writes and unannotated tools ask — and no single stored
         * default says that. External servers go back to asking for everything,
         * which is what a freshly added server does.
         *
         * The cached catalog is dropped and re-read instead of patched the way
         * `saveMcpToolPolicy` patches it: the rule that now decides each row
         * lives in the service, and a second copy of it here is exactly the
         * drift worth avoiding. Re-reading is cheap — without `refresh` an
         * external server is answered from the service's own cache, undialled.
         */
        async resetMcpToolPolicy({ dispatch, rootState }, { serverId }) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/${serverId}/policy`,
                rootState,
            );
            const result = await axios.delete(endpoint, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => response?.data)
            // Re-read over the cached copy rather than dropping it first: the
            // list stays on screen while this is in flight instead of blinking
            // through "no tools" on the way to the same rows.
            await dispatch('getMcpServerTools', { serverId, force: true })
            return result
        },

        /**
         * Answer an in-chat approval prompt. This write is what releases the
         * waiting stream, so it is the one request in this module that a user is
         * actively blocked on — the caller surfaces failures rather than
         * swallowing them, since a lost answer means a turn that times out.
         *
         * The answer covers this one call and stores nothing standing: which
         * tools ask is the client's policy, changed under PAICE settings.
         */
        async sendMcpToolDecision({ rootState }, { resourceId, toolUseId, decision, serverId, toolName }) {
            const endpoint = mergeAgentsQueryIntoUrl(
                `${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/mcp-servers/decision`,
                rootState,
            );
            return axios.post(endpoint, { resourceId, toolUseId, decision, serverId, toolName }, {
                headers: { Authorization: `Bearer ${rootState.accessToken}` },
            }).then((response) => response?.data)
        },
    }
}

export default store
